import type {
  Privilege,
  PrivilegeArea,
  PrivilegeCenter,
  Profile,
} from "../store/slices/profileSlice";

/**
 * Fields extracted from a cost center row needed for privilege comparison.
 * Maps to the API fields: kpGlRegionCd, kpAreaCd, kpSvcArea, glLocCd.
 */
export interface CostCenterPrivilegeFields {
  /** Region code of the cost center (kpGlRegionCd). */
  regionCode: string;
  /** Area code of the cost center (kpAreaCd). */
  areaCode: string;
  /** Service area / center code of the cost center (kpSvcArea). */
  centerCode: string;
  /** Location code of the cost center (glLocCd). */
  locationCode: string;
}

/**
 * Checks whether a location matches within a privilege center's location list.
 * If locations is null, all locations are considered accessible.
 * @param center - The privilege center to check.
 * @param locationCode - The cost center location code.
 * @returns True if the location is permitted.
 */
const isLocationPermitted = (
  center: PrivilegeCenter,
  locationCode: string,
): boolean => {
  if (center.locations === null) {
    return true;
  }
  return center.locations.some(
    (loc) => loc.locationCode === locationCode,
  );
};

/**
 * Checks whether a center and location match within a privilege area's center list.
 * If centers is null, all centers and locations are considered accessible.
 * @param area - The privilege area to check.
 * @param centerCode - The cost center service area code.
 * @param locationCode - The cost center location code.
 * @returns True if the center and location are permitted.
 */
const isCenterPermitted = (
  area: PrivilegeArea,
  centerCode: string,
  locationCode: string,
): boolean => {
  if (area.centers === null) {
    return true;
  }
  return area.centers.some(
    (c) =>
      c.center.centerCode === centerCode &&
      isLocationPermitted(c, locationCode),
  );
};

/**
 * Checks whether a cost center's area, center, and location match within a privilege's area list.
 * If areas is null, all sub-levels are considered accessible for that region.
 * @param privilege - The region-level privilege entry.
 * @param fields - The cost center fields to match.
 * @returns True if the area hierarchy is permitted.
 */
const isAreaPermitted = (
  privilege: Privilege,
  fields: CostCenterPrivilegeFields,
): boolean => {
  if (privilege.areas === null) {
    return true;
  }
  return privilege.areas.some(
    (a) =>
      a.area.areaCode === fields.areaCode &&
      isCenterPermitted(a, fields.centerCode, fields.locationCode),
  );
};

/**
 * Determines whether the user's editDataPrivileges grant edit access to a specific cost center.
 *
 * Matching is hierarchical:
 * - Region must match (kpGlRegionCd === regionCode).
 * - If areas is null, all areas/centers/locations under that region are accessible.
 * - If centers is null within an area, all centers/locations under that area are accessible.
 * - If locations is null within a center, all locations under that center are accessible.
 *
 * @param editDataPrivileges - The editDataPrivileges array from the profile API.
 * @param fields - The cost center fields to check against.
 * @returns True if any privilege entry grants edit access.
 */
export const hasEditPrivilegeForCostCenter = (
  editDataPrivileges: Privilege[],
  fields: CostCenterPrivilegeFields,
): boolean => {
  if (!editDataPrivileges || editDataPrivileges.length === 0) {
    return false;
  }
  return editDataPrivileges.some(
    (privilege) =>
      privilege.region.regionCode === fields.regionCode &&
      isAreaPermitted(privilege, fields),
  );
};

/**
 * Determines whether the logged-in user is an Administrator with edit privileges,
 * suitable for controlling visibility of edit/delete actions across the application.
 *
 * @param profile - The profile object from the Redux store.
 * @returns True if the user is an Administrator with editDataPrivileges present.
 */
export const isAdminWithEditPrivileges = (
  profile: Profile,
): boolean => {
  if (!profile?.primaryRole) {
    return false;
  }
  return (
    profile.primaryRole.toLowerCase() === "administrator" &&
    Array.isArray(profile.editDataPrivileges) &&
    profile.editDataPrivileges.length > 0
  );
};

/**
 * Convenience function combining role check and per-row privilege evaluation.
 * Returns true only when the user is an Administrator AND has edit privilege for the given cost center.
 *
 * @param profile - The profile object from the Redux store.
 * @param fields - The cost center fields to check against.
 * @returns True if the user can edit/delete the specified cost center.
 */
export const canEditCostCenter = (
  profile: Profile,
  fields: CostCenterPrivilegeFields,
): boolean => {
  if (!isAdminWithEditPrivileges(profile)) {
    return false;
  }
  return hasEditPrivilegeForCostCenter(
    profile.editDataPrivileges,
    fields,
  );
};

// ---------------------------------------------------------------------------
// Dropdown-filtering helpers
// Each returns null when all values are allowed (privilege level is null),
// or a Set of allowed codes when the privilege restricts access.
// ---------------------------------------------------------------------------

/**
 * Returns the set of area codes the user may select for a given region,
 * or null when all areas are allowed (areas is null in every matching privilege).
 */
export const getAllowedAreaCodes = (
  editDataPrivileges: Privilege[],
  regionCode: string,
): Set<string> | null => {
  if (!editDataPrivileges || editDataPrivileges.length === 0) return new Set();

  const matchingPrivileges = editDataPrivileges.filter(
    (p) => p.region.regionCode === regionCode,
  );
  if (matchingPrivileges.length === 0) return new Set();

  // If any matching privilege has areas === null, all areas are allowed
  if (matchingPrivileges.some((p) => p.areas === null)) return null;

  const codes = new Set<string>();
  matchingPrivileges.forEach((p) => {
    p.areas?.forEach((a) => codes.add(a.area.areaCode));
  });
  return codes;
};

/**
 * Returns the set of center codes the user may select for a given region + area,
 * or null when all centers are allowed.
 */
export const getAllowedCenterCodes = (
  editDataPrivileges: Privilege[],
  regionCode: string,
  areaCode: string,
): Set<string> | null => {
  if (!editDataPrivileges || editDataPrivileges.length === 0) return new Set();

  const matchingAreas: PrivilegeArea[] = [];
  editDataPrivileges
    .filter((p) => p.region.regionCode === regionCode)
    .forEach((p) => {
      if (p.areas === null) return; // all allowed at area level — handled by caller
      p.areas
        .filter((a) => a.area.areaCode === areaCode)
        .forEach((a) => matchingAreas.push(a));
    });

  // If the region privilege had areas === null, all centers are allowed
  const regionHasNullAreas = editDataPrivileges.some(
    (p) => p.region.regionCode === regionCode && p.areas === null,
  );
  if (regionHasNullAreas) return null;

  if (matchingAreas.length === 0) return new Set();

  // If any matching area has centers === null, all centers are allowed
  if (matchingAreas.some((a) => a.centers === null)) return null;

  const codes = new Set<string>();
  matchingAreas.forEach((a) => {
    a.centers?.forEach((c) => codes.add(c.center.centerCode));
  });
  return codes;
};

/**
 * Returns the set of location codes the user may select for a given region + area + center,
 * or null when all locations are allowed.
 */
export const getAllowedLocationCodes = (
  editDataPrivileges: Privilege[],
  regionCode: string,
  areaCode: string,
  centerCode: string,
): Set<string> | null => {
  if (!editDataPrivileges || editDataPrivileges.length === 0) return new Set();

  // If the region privilege had areas === null, everything below is allowed
  const regionHasNullAreas = editDataPrivileges.some(
    (p) => p.region.regionCode === regionCode && p.areas === null,
  );
  if (regionHasNullAreas) return null;

  const matchingCenters: PrivilegeCenter[] = [];
  editDataPrivileges
    .filter((p) => p.region.regionCode === regionCode)
    .forEach((p) => {
      p.areas
        ?.filter((a) => a.area.areaCode === areaCode)
        .forEach((a) => {
          // If centers is null for this area, all centers/locations are allowed
          if (a.centers === null) return;
          a.centers
            .filter((c) => c.center.centerCode === centerCode)
            .forEach((c) => matchingCenters.push(c));
        });
    });

  // Check if any matched area had centers === null
  const areaHasNullCenters = editDataPrivileges.some((p) => {
    if (p.region.regionCode !== regionCode || p.areas === null) return false;
    return p.areas
      .filter((a) => a.area.areaCode === areaCode)
      .some((a) => a.centers === null);
  });
  if (areaHasNullCenters) return null;

  if (matchingCenters.length === 0) return new Set();

  // If any matching center has locations === null, all locations are allowed
  if (matchingCenters.some((c) => c.locations === null)) return null;

  const codes = new Set<string>();
  matchingCenters.forEach((c) => {
    c.locations?.forEach((l) => codes.add(l.locationCode));
  });
  return codes;
};
